'use client';

// BL-2491 — the shared machinery for a server-minted private topic (BUG-1938's
// `usePrivateTopics`, lifted so the staff Inbox count reuses it: ADR 044 — Must
// Consolidate). A private topic ends in an HMAC token only the server can mint,
// so each surface that listens asks the server for its topic string.
//
// One request per key per tab: the promise is shared across every caller, so
// five mounted surfaces cost one request. A failed request is evicted and
// retried — on demand (`retry`), and on the next window focus or network
// `online` — because the shell that holds these surfaces never remounts, so a
// one-off blip would otherwise leave them dark for the session. A value loaded
// for one key is never served under another, even for one render.

import { useCallback, useEffect, useState } from 'react';

/** What {@link createMintedTopicsHook} is built from. */
export interface MintedTopicsOptions<T extends object> {
    /**
     * Mint the topics for one key. Rejects on failure. The key is the cache key
     * (and may carry what the request needs); the server derives the topics from
     * the session, never from it.
     */
    load: (key: string) => Promise<T>;
    /** The value served while loading, for the empty key, and after a failure. */
    empty: T;
}

/** What a minted-topics hook returns: the topics, any failure, and a way to re-ask. */
export type MintedTopicsResult<T extends object> = T & {
    /** Set when the topics could not be minted; `null` otherwise. */
    error: Error | null;
    /** Mint again after a failure. A no-op while the topics are known. */
    retry: () => void;
};

/** One settled load, tagged with the key it belongs to. */
interface Settled<T> {
    key: string;
    value: T;
    error: Error | null;
}

/**
 * Build a hook that mints (once per key) a set of private topics.
 *
 * Each call creates its own cache and its own set of failed callers, so two
 * hooks never share a key space.
 *
 * @param options - see {@link MintedTopicsOptions}.
 * @returns `useMintedTopics(key)`: the topics for `key` (`empty` until known,
 *   and for the key `''`, which loads nothing), the failure, and `retry`.
 */
export function createMintedTopicsHook<T extends object>({
    load,
    empty,
}: MintedTopicsOptions<T>): (key: string) => MintedTopicsResult<T> {
    /** In-flight or settled load, per key. */
    const cache = new Map<string, Promise<T>>();
    /**
     * Every mounted caller whose last load failed. A retry from ANY caller (a
     * "Try again", a focus, `online`) wakes them all; the shared cache folds
     * their re-runs into one request.
     */
    const failedCallers = new Set<() => void>();

    /** Ask every failed caller to load again. */
    function retryAllFailed(): void {
        for (const rerun of [...failedCallers]) rerun();
    }

    /**
     * The shared load for `key`; a failed one is evicted so the next ask retries.
     *
     * @sideEffect Calls `load(key)` on the first ask per key.
     */
    function loadShared(key: string): Promise<T> {
        const cached = cache.get(key);
        if (cached) return cached;
        const pending = load(key);
        cache.set(key, pending);
        pending.catch(() => cache.delete(key));
        return pending;
    }

    /**
     * The topics for `key`.
     *
     * @param key - the cache key; `''` loads nothing.
     * @returns the topics, the failure and `retry`.
     * @sideEffect While the last load failed, listens for window `focus` and
     *   `online` and retries on either.
     */
    return function useMintedTopics(key: string): MintedTopicsResult<T> {
        const [settled, setSettled] = useState<Settled<T>>({ key: '', value: empty, error: null });
        // Bumped by a retry; re-runs the load effect. The failed promise is
        // already evicted from the cache, so the re-run issues a fresh request.
        const [attempt, setAttempt] = useState(0);
        const current = key !== '' && settled.key === key;
        const failed = current && settled.error != null;
        const retry = useCallback(() => {
            if (failed) retryAllFailed();
        }, [failed]);

        useEffect(() => {
            if (!key) return;
            let cancelled = false;
            loadShared(key).then(
                (value) => {
                    if (!cancelled) setSettled({ key, value, error: null });
                },
                (err: unknown) => {
                    if (cancelled) return;
                    const error = err instanceof Error ? err : new Error(String(err));
                    setSettled({ key, value: empty, error });
                },
            );
            return () => {
                cancelled = true;
            };
        }, [key, attempt]);

        useEffect(() => {
            if (!failed) return;
            const rerun = () => setAttempt((n) => n + 1);
            failedCallers.add(rerun);
            window.addEventListener('focus', retryAllFailed);
            window.addEventListener('online', retryAllFailed);
            return () => {
                failedCallers.delete(rerun);
                window.removeEventListener('focus', retryAllFailed);
                window.removeEventListener('online', retryAllFailed);
            };
        }, [failed]);

        return current ? { ...settled.value, error: settled.error, retry } : { ...empty, error: null, retry };
    };
}
